import { useCallback, useMemo, useRef } from "react";
import {
  NetworkStatus,
  type DocumentNode,
  type ObservableQuery,
  type OperationVariables,
  type TypedDocumentNode,
  type Unmasked,
} from "@apollo/client";

import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";

import type {
  EntityViewAccessSummary,
  ItemAccessMember,
  ItemAccessSummary,
  ItemAccessTeam,
  ObjectAccessLevel,
  PrincipalType,
  WorkflowAccessMember,
  WorkflowAccessSummary,
  WorkflowAccessTeam,
} from "#app/lib/data";
import { DEFAULT_PAGE_SIZE } from "#app/lib/constants/page-size";
import { AnalyticsContext, SharingEvent, useAnalytics } from "#app/lib/analytics";
import type { LiveRefetchContext } from "#app/lib/subscriptions/live-workspace-sync";

// Generic engine behind useItemSharing / useWorkflowSharing /
// useEntityViewSharing. The three GraphQL APIs are deliberate mirrors —
// the same paginated access summary query, the same team-roster query and
// the same share mutations, differing only in the id argument name and
// the root field — so one hook owns the pagination, fetchMore merging,
// refetch wiring and dialog summary; the per-object hooks just supply
// documents and accessors.

export type RawAccessSummary =
  | ItemAccessSummary
  | WorkflowAccessSummary
  | EntityViewAccessSummary;
export type AccessSummaryMember = ItemAccessMember | WorkflowAccessMember;
export type AccessSummaryTeam = ItemAccessTeam | WorkflowAccessTeam;

// The mirror queries take identical variables apart from the id key, so
// this is the concrete shape every document is consumed through.
type SummaryVariables = {
  issueId?: string;
  workflowId?: string;
  entityViewId?: string;
  skip: number;
  limit: number;
  teamSkip: number;
  teamLimit: number;
  search: string | null;
  grantedOnly: boolean;
};

// The two public-tier mutations take the id under the subject's own key.
type PublicAccessLevelVariables = {
  issueId?: string;
  workflowId?: string;
  entityViewId?: string;
  accessLevel: ObjectAccessLevel;
};

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

// The operation name a document declares — what `refetchQueries` takes to
// re-run every active instance of that operation, whatever its variables.
function operationNameOf(document: DocumentNode): string | null {
  for (const def of document.definitions) {
    if (def.kind === "OperationDefinition") return def.name?.value ?? null;
  }
  return null;
}

// What the dialog needs to expand one team: the roster query and how to
// read its rows off the payload. Consumed by `useTeamAccessMembers`.
export type TeamMembersSource = {
  document: DocumentNode;
  idKey: "issueId" | "workflowId" | "entityViewId";
  id: string | null;
  selectMembers: (data: unknown) => AccessSummaryMember[];
};

// What the dialog needs to open the workspace row: the same summary query
// with `grantedOnly` off, so every live member comes back resolved, and
// how to read and page it. Consumed by `useWorkspaceRoster`.
export type WorkspaceRosterSource = {
  document: DocumentNode;
  idKey: "issueId" | "workflowId" | "entityViewId";
  id: string | null;
  selectSummary: (data: unknown) => RawAccessSummary | null;
  mergeMembers: (prev: unknown, next: unknown) => unknown;
};

type ObjectSharingConfig<
  TData,
  TVars extends OperationVariables,
  TSummary extends RawAccessSummary,
> = {
  id: string | null;
  // The per-object hooks forward their own optional `search` straight through,
  // so an absent key and an explicit `undefined` are the same empty search.
  search?: string | undefined;
  // Name of the id variable shared by the summary query and every
  // mutation in `mutations`.
  idKey: "issueId" | "workflowId" | "entityViewId";
  summaryDocument: TypedDocumentNode<TData, TVars>;
  teamMembersDocument: DocumentNode;
  mutations: {
    share: DocumentNode;
    shareTeam: DocumentNode;
    unshare: DocumentNode;
    setVisibility: DocumentNode;
    // The tier public visibility grants. Absent on a subject that has no
    // tier of its own (an item reads its workflow's), so the dialog shows
    // the level as text there.
    setPublicAccessLevel?: DocumentNode;
  };
  // Pull the summary root field off the query payload. Module-scope in
  // the per-object hook so callback identities stay stable.
  selectSummary: (data: TData | undefined) => TSummary | null;
  // Pull the roster rows off the team-members payload. Module-scope too.
  selectTeamMembers: (data: unknown) => AccessSummaryMember[];
  // Rebuild a query payload with one list appended after a fetchMore;
  // `list` is which page was fetched (the other list keeps `prev`'s
  // slice). Lives in the per-object hook so it stays fully typed
  // against that query's shape.
  mergePage: (
    prev: Unmasked<TData>,
    next: Unmasked<TData>,
    list: "members" | "teams",
  ) => Unmasked<TData>;
  // Optional post-visibility-flip hook (workflow sharing re-dispatches
  // AppData so the sidebar's lock chip updates).
  onVisibilityChange?: () => void;
};

const SHARING_OBJECT = {
  issueId: "issue",
  workflowId: "workflow",
  entityViewId: "dashboard",
} as const;

export function useObjectSharing<
  TData,
  TVars extends OperationVariables,
  TSummary extends RawAccessSummary,
>({
  id,
  search,
  idKey,
  summaryDocument,
  teamMembersDocument,
  mutations,
  selectSummary,
  selectTeamMembers,
  mergePage,
  onVisibilityChange,
}: ObjectSharingConfig<TData, TVars, TSummary>) {
  // The query's id variable name is erased on purpose — `baseVariables`
  // builds it from `idKey`, so the document is consumed through the
  // loosely-typed vars view at this one seam.
  const summaryQuery = summaryDocument as unknown as TypedDocumentNode<
    TData,
    SummaryVariables
  >;
  // At rest the dialog lists who HAS access. A search term switches to the
  // add path, which has to see every live member and team.
  const searching = search != null && search.length > 0;
  const baseVariables = useMemo(
    (): SummaryVariables => ({
      [idKey]: id ?? "",
      skip: 0,
      limit: PAGE_SIZE,
      teamSkip: 0,
      teamLimit: PAGE_SIZE,
      search: search ?? null,
      grantedOnly: !searching,
    }),
    [idKey, id, search, searching],
  );
  // A live change re-reads every row already loaded, not the first page
  // only. The ref holds the latest page counts; the context stays stable.
  const liveRefetchRef = useRef<() => Promise<unknown>>(() => Promise.resolve());
  const context = useMemo(
    (): LiveRefetchContext => ({ liveRefetch: () => liveRefetchRef.current() }),
    [],
  );
  const queryOptions = useMemo(
    () => ({
      variables: baseVariables,
      skip: !id,
      notifyOnNetworkStatusChange: true,
      context,
    }),
    [baseVariables, id, context],
  );
  const { data, previousData, loading, error, networkStatus, fetchMore, refetch } =
    useQuery(summaryQuery, queryOptions);
  // Re-entrancy guard for the loadMore callbacks. Keyed on networkStatus
  // — NOT raw `loading`, which is also true while a mutation-triggered
  // refetch is in flight (notifyOnNetworkStatusChange + refetchQueries
  // on every share mutation), and would silently drop a pagination
  // request fired during that window.
  const fetchingMore = networkStatus === NetworkStatus.fetchMore;
  // While a search query is in flight, Apollo briefly returns
  // `data: undefined` until the new result arrives — fall back to the
  // last seen payload so the list stays visible instead of swapping
  // in a skeleton. `previousData`'s type unions in `DeepPartial<TData>`
  // for the @defer streaming data state; this app runs no deferred
  // queries, so a previous payload is always complete.
  const displayData = data ?? (previousData as TData | undefined);

  // Mutations refetch every ACTIVE instance of the summary operation (the
  // list at rest, a search, the workspace row's roster) and every active
  // team roster, all by operation name — a level changed on any row
  // refreshes them all, whatever their variables.
  const summaryOperation = operationNameOf(summaryDocument);
  const teamMembersOperation = operationNameOf(teamMembersDocument);
  const refetchQueries = useMemo(
    () =>
      id
        ? [
            ...(summaryOperation ? [summaryOperation] : []),
            ...(teamMembersOperation ? [teamMembersOperation] : []),
          ]
        : [],
    [id, summaryOperation, teamMembersOperation],
  );
  // Refetch by name reaches every registered instance of the operation —
  // including the ones a CLOSED dialog mount holds with no subject (the
  // dialog is mounted in several places at once, each skipping its query
  // with an empty id). Re-running those hits the server with `id: ""` and
  // comes back FORBIDDEN, which the global error link toasts. Only an
  // instance that names a real subject is worth refreshing.
  const onQueryUpdated = useCallback(
    (observable: ObservableQuery) =>
      Boolean((observable.variables as Record<string, unknown> | undefined)?.[idKey]),
    [idKey],
  );
  const refetchOptions = useMemo(
    () => ({ refetchQueries, onQueryUpdated }),
    [refetchQueries, onQueryUpdated],
  );
  const client = useApolloClient();
  const [shareMutation] = useMutation(mutations.share, refetchOptions);
  const [shareTeamMutation] = useMutation(mutations.shareTeam, refetchOptions);
  const [unshareMutation] = useMutation(mutations.unshare, refetchOptions);
  const [setVisibilityMutation] = useMutation(
    mutations.setVisibility,
    refetchOptions,
  );

  // `sharing.*` events name the object kind and the access level, never
  // the object or the principal. Each one waits for its write to land.
  const { track } = useAnalytics();
  const object = SHARING_OBJECT[idKey];
  const trackSharing = useCallback(
    (
      event: (typeof SharingEvent)[keyof typeof SharingEvent],
      data: Record<string, unknown> = {},
    ) => {
      track({ context: AnalyticsContext.Sharing, event, data: { object, ...data } });
    },
    [object, track],
  );

  const setMemberShare = useCallback(
    (memberId: string, accessLevel: ObjectAccessLevel, enabled: boolean) => {
      if (!id) return;
      void shareMutation({
        variables: {
          input: { [idKey]: id, userId: memberId, accessLevel, enabled },
        },
      }).then(() =>
        trackSharing(SharingEvent.SetMemberAccess, { accessLevel, enabled }),
      );
    },
    [shareMutation, idKey, id, trackSharing],
  );

  const setTeamShare = useCallback(
    (teamId: string, accessLevel: ObjectAccessLevel, enabled: boolean) => {
      if (!id) return;
      void shareTeamMutation({
        variables: {
          input: { [idKey]: id, teamId, accessLevel, enabled },
        },
      }).then(() =>
        trackSharing(SharingEvent.SetTeamAccess, { accessLevel, enabled }),
      );
    },
    [shareTeamMutation, idKey, id, trackSharing],
  );

  // Deletes the principal's own share row. Not a deny (that is
  // setMemberShare(…, false)): the principal falls back to whatever other
  // path reaches them — the team's tier, public visibility, nothing.
  const unshare = useCallback(
    (principalType: PrincipalType, principalId: string) => {
      if (!id) return;
      void unshareMutation({
        variables: { input: { [idKey]: id, principalType, principalId } },
      }).then(() => trackSharing(SharingEvent.Unshare, { principal: principalType }));
    },
    [unshareMutation, idKey, id, trackSharing],
  );

  const setVisibility = useCallback(
    (next: "public" | "private") => {
      if (!id) return;
      void setVisibilityMutation({
        variables: { input: { [idKey]: id, visibility: next } },
      }).then(() => {
        trackSharing(SharingEvent.SetVisibility, { visibility: next });
        onVisibilityChange?.();
      });
    },
    [setVisibilityMutation, idKey, id, onVisibilityChange, trackSharing],
  );

  // The tier public visibility grants. Runs through the client directly:
  // a subject without a tier of its own has no document to give a
  // mutation hook, and the hook count cannot change with it.
  const publicAccessLevelDocument = mutations.setPublicAccessLevel;
  const setPublicAccessLevel = useMemo(
    () =>
      publicAccessLevelDocument && id
        ? (accessLevel: ObjectAccessLevel) => {
            const variables: PublicAccessLevelVariables = {
              [idKey]: id,
              accessLevel,
            };
            void client
              .mutate({
                mutation: publicAccessLevelDocument,
                variables,
                refetchQueries,
                onQueryUpdated,
              })
              .then(() =>
                trackSharing(SharingEvent.SetPublicAccess, { accessLevel }),
              );
          }
        : null,
    [
      publicAccessLevelDocument,
      id,
      idKey,
      client,
      refetchQueries,
      onQueryUpdated,
      trackSharing,
    ],
  );

  const summary: TSummary | null = useMemo(
    () => selectSummary(displayData) ?? null,
    [selectSummary, displayData],
  );

  // The "Shared with" group's bulk verbs, over the rows it lists: every
  // loaded team and every loaded member a share can change. One refetch
  // at the end instead of one per row.
  const setAll = useCallback(
    async (accessLevel: ObjectAccessLevel) => {
      if (!id || !summary) return;
      const writes: Promise<unknown>[] = [];
      for (const team of summary.teams) {
        writes.push(
          client.mutate({
            mutation: mutations.shareTeam,
            variables: {
              input: { [idKey]: id, teamId: team.id, accessLevel, enabled: team.enabled },
            },
          }),
        );
      }
      for (const member of summary.members) {
        if (member.locked) continue;
        writes.push(
          client.mutate({
            mutation: mutations.share,
            variables: {
              input: { [idKey]: id, userId: member.id, accessLevel, enabled: member.enabled },
            },
          }),
        );
      }
      await Promise.all(writes);
      trackSharing(SharingEvent.SetAll, { accessLevel, count: writes.length });
      await client.refetchQueries({ include: refetchQueries, onQueryUpdated });
    },
    [
      id,
      summary,
      client,
      mutations.shareTeam,
      mutations.share,
      idKey,
      refetchQueries,
      onQueryUpdated,
      trackSharing,
    ],
  );

  // Removing is what the row's own removal would do: a share row is
  // deleted so the principal falls back to whatever else reaches them; a
  // derived path (an assignee) cannot be cut here, so it is denied. A team
  // on the item's Teams property is not a share and is left alone.
  const removeAll = useCallback(async () => {
    if (!id || !summary) return;
    const writes: Promise<unknown>[] = [];
    for (const team of summary.teams) {
      if (team.source === "team") continue;
      writes.push(
        client.mutate({
          mutation: mutations.unshare,
          variables: { input: { [idKey]: id, principalType: "team", principalId: team.id } },
        }),
      );
    }
    for (const member of summary.members) {
      if (member.locked) continue;
      writes.push(
        member.source === "shared" || !member.enabled
          ? client.mutate({
              mutation: mutations.unshare,
              variables: {
                input: { [idKey]: id, principalType: "user", principalId: member.id },
              },
            })
          : client.mutate({
              mutation: mutations.share,
              variables: {
                input: {
                  [idKey]: id,
                  userId: member.id,
                  accessLevel: member.accessLevel,
                  enabled: false,
                },
              },
            }),
      );
    }
    await Promise.all(writes);
    trackSharing(SharingEvent.RemoveAll, { count: writes.length });
    await client.refetchQueries({ include: refetchQueries, onQueryUpdated });
  }, [
    id,
    summary,
    client,
    mutations.unshare,
    mutations.share,
    idKey,
    refetchQueries,
    onQueryUpdated,
    trackSharing,
  ]);
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const loadedMembers = summary?.members.length ?? 0;
  // Use the filtered counts for pagination decisions so an active
  // search doesn't keep `hasMore` true after the last matching row
  // is already on screen.
  const totalFilteredMembers = summary?.filteredMemberCount ?? 0;
  const hasMoreMembers = loadedMembers < totalFilteredMembers;
  const loadedTeams = summary?.teams.length ?? 0;
  const totalFilteredTeams = summary?.filteredTeamCount ?? 0;
  const hasMoreTeams = loadedTeams < totalFilteredTeams;
  liveRefetchRef.current = () =>
    refetch({
      ...baseVariables,
      limit: Math.max(PAGE_SIZE, loadedMembers),
      teamLimit: Math.max(PAGE_SIZE, loadedTeams),
    });

  const loadMoreMembers = useCallback(() => {
    if (!id || !hasMoreMembers || fetchingMore) return;
    void fetchMore({
      // Re-request page 0 of teams so the merge result stays stable;
      // mergePage throws the team slice away.
      variables: { ...baseVariables, skip: loadedMembers },
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult ? mergePage(prev, fetchMoreResult, "members") : prev,
    });
  }, [fetchMore, hasMoreMembers, loadedMembers, fetchingMore, id, baseVariables, mergePage]);

  const loadMoreTeams = useCallback(() => {
    if (!id || !hasMoreTeams || fetchingMore) return;
    void fetchMore({
      variables: { ...baseVariables, teamSkip: loadedTeams },
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult ? mergePage(prev, fetchMoreResult, "teams") : prev,
    });
  }, [fetchMore, hasMoreTeams, loadedTeams, fetchingMore, id, baseVariables, mergePage]);

  const teamMembers: TeamMembersSource = useMemo(
    () => ({
      document: teamMembersDocument,
      idKey,
      id,
      selectMembers: selectTeamMembers,
    }),
    [teamMembersDocument, idKey, id, selectTeamMembers],
  );

  const workspaceRoster: WorkspaceRosterSource = useMemo(
    () => ({
      document: summaryDocument,
      idKey,
      id,
      selectSummary: (payload) => selectSummary(payload as TData | undefined),
      mergeMembers: (prev, next) =>
        mergePage(prev as Unmasked<TData>, next as Unmasked<TData>, "members"),
    }),
    [summaryDocument, idKey, id, selectSummary, mergePage],
  );

  return {
    /** The object kind, for analytics: "issue", "workflow" or "dashboard". */
    object,
    trackSharing,
    summary,
    // True while the FIRST payload is still in flight; a search or a
    // mutation refetch keeps the previous payload on screen instead.
    loading: loading && !summary,
    // The first payload failed. Without this a failed summary reads as
    // "still loading" (no payload, not loading) and the dialog shows its
    // skeleton forever.
    failed: !loading && !summary && error != null,
    retry,
    // True while the add path's search is resolving — the results list
    // stays put and the field shows the pending state.
    searching: searching && loading,
    hasMoreMembers,
    loadMoreMembers,
    hasMoreTeams,
    loadMoreTeams,
    setMemberShare,
    setTeamShare,
    unshare,
    setVisibility,
    setPublicAccessLevel,
    setAll,
    removeAll,
    teamMembers,
    workspaceRoster,
  };
}

export type ObjectSharing = ReturnType<typeof useObjectSharing>;
